import type { MediaSource } from './mediaReview';
import { frameAt, type VideoFramePosition } from './videoFrames';
const serverOrigin=window.location.origin;
const serverFetch:typeof fetch=(url,options)=>fetch(url,{...options,credentials:'same-origin',headers:{...options?.headers,'X-Skrepka-Request':'1'}});

export interface OriginalVideoIndex { status:'ready'; version:string; positions:VideoFramePosition[]; width:number; height:number; duration:number; codec:string }
export function originalVideoEndpoint(source:MediaSource) {
  const path=new URL(source.url,serverOrigin).pathname;
  const shared=/^\/(?:s\/|api\/explorer\/public\/)(?:[A-Za-z0-9_-]{22}|[a-f0-9]{64})(?:\/content|\/folder\/view)?$/.test(path);
  return '/api/explorer/mobile-video/'+(shared?'public-original/':'original/');
}
const pause = (signal:AbortSignal) => new Promise<void>((resolve,reject) => {
  const abort=()=>{clearTimeout(timer);signal.removeEventListener('abort',abort);reject(new DOMException('Aborted','AbortError'));};
  const timer=setTimeout(()=>{signal.removeEventListener('abort',abort);resolve();},500);
  signal.addEventListener('abort',abort,{once:true});if(signal.aborted)abort();
});
export function originalVideoPayload(source:MediaSource) {
  const url=new URL(source.url,serverOrigin);
  if(url.origin!==serverOrigin && !['tab','skrepka.myxa.tv'].includes(url.hostname))throw new Error('Оригинал находится на другом сервере.');
  return {url:url.pathname+url.search,context:source.videoContext||{}};
}
export function validOriginalIndex(value:unknown):value is OriginalVideoIndex {
  if(!value||typeof value!=='object')return false;
  const data=value as OriginalVideoIndex;
  return data.status==='ready'&&/^[a-f0-9]{64}$/.test(data.version)&&Number.isInteger(data.width)&&data.width>0&&Number.isInteger(data.height)&&data.height>0&&data.width*data.height<=36_000_000&&Math.max(data.width,data.height)<=8192&&Number.isFinite(data.duration)&&data.duration>0&&typeof data.codec==='string'&&Array.isArray(data.positions)&&data.positions.length>0&&data.positions.length<=500000&&data.positions.every((frame,n)=>Number.isFinite(frame.time)&&frame.time>=0&&Number.isFinite(frame.duration)&&frame.duration>=0&&(!n||frame.time>data.positions[n-1].time));
}
export async function readOriginalVideoIndex(source:MediaSource,signal:AbortSignal):Promise<OriginalVideoIndex> {
  const body=JSON.stringify(originalVideoPayload(source)),until=Date.now()+180000;
  while(Date.now()<until){
    signal.throwIfAborted();
    const response=await serverFetch(originalVideoEndpoint(source)+'index',{method:'POST',signal,headers:{'Content-Type':'application/json'},body});
    if(!response.ok)throw new Error('Не удалось прочитать оригинал. Проверьте доступ к видео.');
    const {data}=await response.json() as {data?:OriginalVideoIndex|{status:string;message?:string}};
    if(validOriginalIndex(data))return data;
    if(!data||!['queued','processing','busy'].includes(data.status))throw new Error(data&&'message' in data&&data.message||'Не удалось получить точный индекс оригинала.');
    await pause(signal);
  }
  throw new Error('Индекс оригинала ещё не готов. Повторите открытие видео.');
}
export async function captureOriginalVideoFrame(source:MediaSource,index:OriginalVideoIndex,time:number,signal:AbortSignal) {
  const number=frameAt(index.positions,time),position=index.positions[number];
  const body=JSON.stringify({...originalVideoPayload(source),version:index.version,number}),until=Date.now()+60000;
  while(Date.now()<until){
    signal.throwIfAborted();
    const response=await serverFetch(originalVideoEndpoint(source)+'frame',{method:'POST',signal,headers:{'Content-Type':'application/json'},body});
    if(response.status===202){await pause(signal);continue;}
    if(!response.ok||!response.headers.get('Content-Type')?.startsWith('image/png'))throw new Error(response.status===409?'Оригинал изменился. Откройте видео заново.':'Не удалось получить кадр оригинала.');
    const blob=await response.blob();signal.throwIfAborted();
    if(!blob.size||blob.size>256*1024*1024)throw new Error('Неверный размер кадра оригинала.');
    return {url:URL.createObjectURL(blob),name:`${source.name.replace(/\.[^.]+$/,'')}-frame-${number+1}.png`,time:position.time,number:number+1};
  }
  throw new Error('Кадр ещё загружается. Повторите выбор.');
}
